import React from 'react';
import {
  Globe2,
  MessageCircleMore,
  MousePointerClick,
  Sparkles,
  Layers3,
  Languages,
} from 'lucide-react';

import { MEDIA } from '../../constants/media';

type Feature = {
  icon: React.ReactNode;
  title: string;
  description: string;
  badgeClass: string;
};

const FEATURES: Feature[] = [
  {
    icon: <Globe2 className="h-5 w-5 text-blue-600" />,
    title: 'Works across time zones',
    description:
      'See everyone’s availability in their local time so global teams can align without doing time math.',
    badgeClass: 'bg-blue-100',
  },
  {
    icon: <MessageCircleMore className="h-5 w-5 text-green-700" />,
    title: 'Simple for participants',
    description:
      'Share a single link. Guests just type their name and tap or drag to mark when they’re free.',
    badgeClass: 'bg-green-100',
  },
  {
    icon: <MousePointerClick className="h-5 w-5 text-yellow-700" />,
    title: 'One-click best time',
    description:
      'Instantly spot the time slot that works for the most people with clear, color‑coded overlaps.',
    badgeClass: 'bg-yellow-100',
  },
  {
    icon: <Layers3 className="h-5 w-5 text-purple-700" />,
    title: 'Multiple plans at once',
    description:
      'Create meeting planners for different teams, projects, or events without juggling endless threads.',
    badgeClass: 'bg-purple-100',
  },
  {
    icon: <Sparkles className="h-5 w-5 text-pink-600" />,
    title: 'Clean, modern interface',
    description:
      'A distraction‑free layout that feels familiar to everyone, from busy teams to friends planning a meeting.',
    badgeClass: 'bg-pink-100',
  },
  {
    icon: <Languages className="h-5 w-5 text-indigo-600" />,
    title: 'Flexible for any group',
    description:
      'Use WhenWeMeet for team standups, family gatherings, study sessions, or community events of all sizes.',
    badgeClass: 'bg-indigo-100',
  },
];

export default function Features() {
  return (
    <section id="features" className="pt-12 sm:pt-16 md:pt-20 px-4 bg-white">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-2">
          Powerful features to elevate your planning
        </h2>

        <p className="text-gray-600 text-sm sm:text-base text-center max-w-2xl mx-auto">
          Make it effortless for everyone to share when they’re free, compare
          availability, and lock in the perfect time to meet.
        </p>

        <div className="max-w-4xl mx-auto">
          <img
            src={MEDIA.hero}
            alt="WhenWeMeet product screenshots on MacBook and iPhone"
            className="w-full h-auto"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="flex flex-col rounded-2xl border border-gray-200 bg-white px-5 py-6 text-left shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full ${feature.badgeClass}`}>
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                  {feature.title}
                </h3>
              </div>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
